import { t } from 'i18next'

import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'

export interface PermissionDefinition {
  key: string
  label: string
  info?: string
  parent?: string
  children?: PermissionDefinition[]
  type?: 'toggle' | 'radio'
  radioOptions?: { label: string; value: string; disabled?: boolean; disabledInfo?: string }[]
}

export interface PermissionCategory {
  id: string
  title: string
  icon: string
  saveButtonLabel: string
  permissions: PermissionDefinition[]
}

export const SCHEDULE_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'schedule__team_view_access',
    label: t('core:locationPermissions.schedule.teamViewAccess', 'Team view access'),
  },
  {
    key: 'schedule__booking_for_all_team_members',
    label: t(
      'core:locationPermissions.schedule.bookingForAllTeamMembers',
      'Booking for all team members'
    ),
  },
  {
    key: 'schedule__booking_requests',
    label: t('core:locationPermissions.schedule.bookingRequests', 'Booking requests'),
  },
  {
    key: 'schedule__allow_as_instructor_for_schedule',
    label: t(
      'core:locationPermissions.schedule.allowAsInstructor',
      'Allow as instructor for schedule'
    ),
  },
  {
    key: 'schedule__sessions__add_and_edit',
    label: t('core:locationPermissions.schedule.addAndEditSessions', 'Add and edit Sessions'),
  },
  {
    key: 'schedule__mark_attendance',
    label: t('core:locationPermissions.schedule.markAttendance', 'Mark attendance'),
  },
  {
    key: 'schedule__sessions__cancel',
    label: t('core:locationPermissions.schedule.cancelSessions', 'Cancel sessions'),
  },
  {
    key: 'schedule__sessions__delete',
    label: t('core:locationPermissions.schedule.deleteSessions', 'Delete sessions'),
  },
  {
    key: 'schedule__allow_custom_session_creation',
    label: t('core:locationPermissions.schedule.allowCustomSessionCreation'),
  },
  {
    key: 'schedule__edit_of_employee_work_hours',
    label: t('core:locationPermissions.schedule.editEmployeeWorkHours', 'Edit employee work hours'),
  },
  {
    key: 'schedule__add_or_remove_booked_clients',
    label: t(
      'core:locationPermissions.schedule.addRemoveBookedClients',
      'Add and remove booked clients'
    ),
  },
  {
    key: 'schedule__communicate_with_clients_within_schedule',
    label: t(
      'core:locationPermissions.schedule.communicateWithClients',
      'Communicate with clients within Schedule'
    ),
  },
  {
    key: 'schedule__enable_late_cancel_and_refund',
    label: t(
      'core:locationPermissions.schedule.enableLateCancelRefund',
      'Enable late cancel and refund'
    ),
  },
  {
    key: 'schedule__soap_notes__read',
    label: t('core:locationPermissions.schedule.soapNotes', 'SOAP notes'),
    children: [
      {
        key: 'schedule__soap_notes__read',
        label: t('core:locationPermissions.schedule.viewSoapNotes', 'View SOAP session notes'),
        parent: 'schedule__soap_notes__read',
      },
      {
        key: 'schedule__soap_notes__write',
        label: t(
          'core:locationPermissions.schedule.addEditSoapNotes',
          'Add and edit of SOAP session notes'
        ),
        parent: 'schedule__soap_notes__read',
      },
    ],
  },
  {
    key: 'schedule__allow_bookings_for_team_outside_of_employee_work_hours',
    label: t(
      'core:locationPermissions.schedule.bookingsOutsideWorkHours',
      'Bookings for team outside of employee work hours'
    ),
  },
  {
    key: 'schedule__enable_sub_instructor',
    label: t('core:locationPermissions.schedule.subInstructor', 'Sub instructor'),
    children: [
      {
        key: 'schedule__enable_sub_instructor_edit_instructor',
        label: t('core:locationPermissions.schedule.editInstructor', 'Edit Instructor'),
        parent: 'schedule__enable_sub_instructor',
      },
    ],
  },
  {
    key: 'schedule__covers__request',
    label: t('core:locationPermissions.schedule.coversRequest'),
  },
  {
    key: 'schedule__covers__claim',
    label: t('core:locationPermissions.schedule.coversClaim'),
  },
  {
    key: 'schedule__covers__approve',
    label: t('core:locationPermissions.schedule.coversApprove'),
  },
  {
    key: 'schedule__covers__manage_qualifications',
    label: t('core:locationPermissions.schedule.coversManageQualifications'),
  },
  {
    key: 'schedule__bulk_cancel_sessions',
    label: t('core:locationPermissions.schedule.bulkCancelSessions', 'Bulk cancel sessions'),
  },
  {
    key: 'schedule__manage_site_views',
    label: t('core:locationPermissions.schedule.manageSiteViews', 'Manage site schedule views'),
  },
]

// Retail & Payments Permissions (19 total)
export const RETAIL_PAYMENTS_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'payments__request_payment_access',
    label: t(
      'core:locationPermissions.retailPayments.requestPaymentAccess',
      'Request payment access'
    ),
  },
  {
    key: 'payments__process_payments_within_schedule',
    label: t(
      'core:locationPermissions.retailPayments.processPaymentsWithinSchedule',
      'Process payments within schedule'
    ),
  },
  {
    key: 'payments__access_to_cash_draw',
    label: t('core:locationPermissions.retailPayments.accessToCashDraw', 'Access to cash draw'),
  },
  {
    key: 'payments__change_client_payment_method',
    label: t(
      'core:locationPermissions.retailPayments.changeClientPaymentMethod',
      'Change client payment method'
    ),
  },
  {
    key: 'payments__send_payment_request_email',
    label: t(
      'core:locationPermissions.retailPayments.sendPaymentRequestEmail',
      'Send payment request email'
    ),
  },
  {
    key: 'payments__retry_failed_payment_with_payment_method',
    label: t(
      'core:locationPermissions.retailPayments.retryFailedPayment',
      'Retry failed payment with payment method'
    ),
  },
  {
    key: 'payments__edit_dishonor_fee',
    label: t('core:locationPermissions.retailPayments.editDishonorFee', 'Editing Dishonor Fee'),
  },
  {
    key: 'payments__service_cancellation',
    label: t('core:locationPermissions.retailPayments.serviceCancellation'),
    children: [
      {
        key: 'payments__service_cancellation_notice_override',
        label: t('core:locationPermissions.retailPayments.serviceCancellationNoticeOverride'),
        info: t('core:locationPermissions.retailPayments.serviceCancellationNoticeOverrideInfo'),
        parent: 'payments__service_cancellation',
      },
    ],
  },
  {
    key: 'payments__service_plan_change',
    label: t('core:locationPermissions.retailPayments.servicePlanChange', 'Membership plan change'),
  },
  {
    key: 'retail__sales__sell_outside_dynamic_display',
    label: t('core:locationPermissions.retailPayments.sellOutsideDynamicDisplay'),
    info: t('core:locationPermissions.retailPayments.sellOutsideDynamicDisplayInfo'),
  },
  {
    key: 'payments__service_credit_adjustment',
    label: t(
      'core:locationPermissions.retailPayments.serviceCreditAdjustment',
      'Membership and Package credit adjustment'
    ),
    info: t(
      'core:locationPermissions.retailPayments.serviceCreditAdjustmentInfo',
      "Allows adding or removing credits on a client's memberships and packages with Update credits."
    ),
  },
  {
    key: 'clients__bulk_edit_packages_expiry_date',
    label: t('core:locationPermissions.retailPayments.editPackageExpiryDate'),
    info: t('core:locationPermissions.retailPayments.editPackageExpiryDateInfo'),
  },
  {
    key: 'payments__refund',
    label: t('core:locationPermissions.retailPayments.refund'),
    info: t('core:locationPermissions.retailPayments.refundInfo'),
  },
  {
    key: 'payments__invoice_reinstate',
    label: t('core:locationPermissions.retailPayments.invoiceReinstate'),
    info: t('core:locationPermissions.retailPayments.invoiceReinstateInfo'),
  },
  {
    key: 'retail__sales__sell_outside_availability',
    label: t('core:locationPermissions.retailPayments.sellOutsideAvailability'),
    info: t('core:locationPermissions.retailPayments.sellOutsideAvailabilityInfo'),
  },
  {
    key: 'payments__advanced_checkout',
    label: t('core:locationPermissions.retailPayments.advancedCheckout', 'Advanced checkout'),
    children: [
      {
        key: 'payments__advanced_checkout__credit_activation_date',
        label: t(
          'core:locationPermissions.retailPayments.serviceStartDate',
          'Credit activation date'
        ),
        parent: 'payments__advanced_checkout',
      },
      {
        key: 'payments__advanced_checkout__first_payment_date',
        label: t(
          'core:locationPermissions.retailPayments.serviceFirstBillingDate',
          'First payment date'
        ),
        parent: 'payments__advanced_checkout',
      },
      {
        key: 'payments__advanced_checkout__credit_valid_until',
        label: t('core:locationPermissions.retailPayments.creditValidUntil', 'Credit valid until'),
        parent: 'payments__advanced_checkout',
      },
      {
        key: 'payments__advanced_checkout__auto_renew',
        label: t('core:locationPermissions.retailPayments.autoRenew', 'Auto renew'),
        parent: 'payments__advanced_checkout',
      },
      {
        key: 'payments__advanced_checkout__one_time_setup_fee',
        label: t('core:locationPermissions.retailPayments.oneTimeSetupFee', 'One time setup fee'),
        parent: 'payments__advanced_checkout',
      },
      {
        key: 'payments__advanced_checkout__pay_up_front',
        label: t('core:locationPermissions.retailPayments.payUpFront', 'Pay up front'),
        parent: 'payments__advanced_checkout',
      },
      {
        key: 'payments__advanced_checkout__discount_any_price',
        label: t('core:locationPermissions.retailPayments.discount', 'Discount'),
        parent: 'payments__advanced_checkout',
        type: 'radio',
        // OC-837: checkout can never raise an item's price, so "Set at any price" is meaningless and
        // is disabled; "Set only a Lower price" is the sole selectable/default option. The "any price"
        // bit is kept (not removed) so existing grants that carry it stay valid — enforcement treats
        // either discount bit as "can discount".
        radioOptions: [
          {
            label: t(
              'core:locationPermissions.retailPayments.setOnlyLowerPrice',
              'Set only a Lower price'
            ),
            value: 'payments__advanced_checkout__discount_only_lower_price',
          },
          {
            label: t('core:locationPermissions.retailPayments.setAtAnyPrice', 'Set at any price'),
            value: 'payments__advanced_checkout__discount_any_price',
            disabled: true,
            disabledInfo: t(
              'core:locationPermissions.retailPayments.setAtAnyPriceDisabledInfo',
              'Checkout can only lower an item’s price, so setting a higher price is not available.'
            ),
          },
        ],
      },
    ],
  },
  {
    key: 'payments__bulk_edit_transactions',
    label: t(
      'core:locationPermissions.retailPayments.bulkEditTransactions',
      'Bulk edit transactions'
    ),
  },
  {
    key: 'access__capital',
    label: t('core:locationPermissions.retailPayments.capital', 'Capital'),
    info: t(
      'core:locationPermissions.retailPayments.capitalInfo',
      'View and manage Stripe Capital financing offers, applications and repayments for this location.'
    ),
  },
]

// Access Permissions (29 total)
export const ACCESS_PERMISSIONS_LOCATION: PermissionDefinition[] = [
  {
    key: 'access__dashboards__display_home_snapshot',
    label: t('core:locationPermissions.access.displayHomeSnapshots', 'Display home snapshots'),
    children: [
      {
        key: 'access__dashboards__display_home_revenue_and_retail_snapshot',
        label: t('core:locationPermissions.access.displayHomeRevenueAndRetailSnapshot'),
        parent: 'access__dashboards__display_home_snapshot',
      },
    ],
  },
  {
    key: 'settings__retail',
    label: t('core:locationPermissions.access.retail'),
    children: [
      {
        key: 'settings__retail__packages__read',
        label: t('core:locationPermissions.access.packages', 'Packages'),
        parent: 'settings__retail',
        children: [
          {
            key: 'settings__retail__packages__write',
            label: t('core:locationPermissions.access.addAndEdit', 'Add and edit'),
            parent: 'settings__retail__packages__read',
          },
          {
            key: 'settings__retail__packages__write_restricted',
            label: t('core:locationPermissions.access.editRestricted', 'Edit restricted'),
            parent: 'settings__retail__packages__read',
          },
          {
            key: 'settings__retail__packages__archive',
            label: t('core:locationPermissions.access.archive', 'Archive'),
            parent: 'settings__retail__packages__read',
          },
        ],
      },
      {
        key: 'settings__retail__memberships__read',
        label: t('core:locationPermissions.access.memberships', 'Memberships'),
        parent: 'settings__retail',
        children: [
          {
            key: 'settings__retail__memberships__write',
            label: t('core:locationPermissions.access.addAndEdit', 'Add and edit'),
            parent: 'settings__retail__memberships__read',
          },
          {
            key: 'settings__retail__memberships__write_restricted',
            label: t('core:locationPermissions.access.editRestricted', 'Edit restricted'),
            parent: 'settings__retail__memberships__read',
          },
          {
            key: 'settings__retail__memberships__archive',
            label: t('core:locationPermissions.access.archive', 'Archive'),
            parent: 'settings__retail__memberships__read',
          },
        ],
      },
      {
        key: 'settings__retail__add_ons__read',
        label: t('core:locationPermissions.access.addOns'),
        parent: 'settings__retail',
        children: [
          {
            key: 'settings__retail__add_ons__write',
            label: t('core:locationPermissions.access.addAndEdit'),
            parent: 'settings__retail__add_ons__read',
          },
        ],
      },
      {
        key: 'settings__retail__products__read',
        label: t('core:locationPermissions.access.posInventory', 'POS inventory'),
        parent: 'settings__retail',
        children: [
          {
            key: 'settings__retail__products__write_add',
            label: t('core:locationPermissions.access.addInventory', 'Add inventory'),
            parent: 'settings__retail__products__read',
          },
          {
            key: 'settings__retail__products__write_edit',
            label: t('core:locationPermissions.access.editInventory', 'Edit inventory'),
            parent: 'settings__retail__products__read',
          },
        ],
      },
      {
        key: 'settings__retail__promo_codes',
        label: t('core:locationPermissions.access.promoCodes', 'Promo codes'),
        parent: 'settings__retail',
      },
      {
        key: 'settings__retail__gift_cards__read',
        label: t('core:locationPermissions.access.giftCards', 'Gift cards'),
        parent: 'settings__retail',
        children: [
          {
            key: 'settings__retail__gift_cards__write',
            label: t('core:locationPermissions.access.addAndEdit', 'Add and edit'),
            parent: 'settings__retail__gift_cards__read',
          },
        ],
      },
      {
        key: 'settings__retail__fees',
        label: t('core:locationPermissions.access.retailFees', 'Retail fees'),
        parent: 'settings__retail',
      },
      {
        // INT-67 / CORE-1322. Neutral keys (DD-011); user-facing label is brand "Ezypay" (Q6).
        key: 'settings__retail__account_codes__read',
        label: t(
          'core:locationPermissions.access.ezypayAccountingCodes',
          'Ezypay accounting codes'
        ),
        parent: 'settings__retail',
        children: [
          {
            key: 'settings__retail__account_codes__write',
            label: t(
              'core:locationPermissions.access.manageEzypayAccountingCodes',
              'Manage Ezypay accounting codes'
            ),
            parent: 'settings__retail__account_codes__read',
          },
        ],
      },
      {
        key: 'settings__retail__sale_settings',
        label: t('core:locationPermissions.access.saleSettings', 'Sale settings'),
        parent: 'settings__retail',
      },
      {
        key: 'settings__retail__general_settings',
        label: t('core:locationPermissions.access.generalSettings', 'General settings'),
        parent: 'settings__retail',
      },
      {
        key: 'settings__retail__terminals',
        label: t('core:locationPermissions.access.readers', 'Readers'),
        parent: 'settings__retail',
      },
    ],
  },
  {
    key: 'settings__account',
    label: t('core:locationPermissions.access.accountSettings', 'Account settings'),
    children: [
      {
        key: 'settings__account__billing',
        label: t('core:locationPermissions.access.billing', 'Billing'),
        parent: 'settings__account',
      },
      {
        key: 'settings__account__tax',
        label: t('core:locationPermissions.access.tax', 'Tax'),
        parent: 'settings__account',
      },
      {
        key: 'settings__account__invoice',
        label: t('core:locationPermissions.access.invoice', 'Invoice'),
        parent: 'settings__account',
      },
      {
        key: 'settings__account__point_of_sale',
        label: t('core:locationPermissions.access.pointOfSale', 'Point of Sale'),
        parent: 'settings__account',
      },
      {
        key: 'settings__account__payroll',
        label: t('core:locationPermissions.access.payroll', 'Payroll'),
        parent: 'settings__account',
      },
      {
        key: 'settings__account__commissions',
        label: t('core:locationPermissions.access.commissions', 'Commissions'),
        parent: 'settings__account',
      },
      {
        key: 'settings__account__sms_credits',
        label: t('core:locationPermissions.access.smsCredits'),
        parent: 'settings__account',
        children: [
          {
            key: 'access__notifications__receive_sms_running_low',
            label: t('core:locationPermissions.access.receiveSmsRunningLow'),
            parent: 'settings__account__sms_credits',
          },
        ],
      },
    ],
  },
  {
    key: 'settings__covers',
    label: t('core:locationPermissions.access.covers'),
  },
  {
    key: 'settings__location',
    label: t('core:locationPermissions.access.location', 'Location'),
    children: [
      {
        key: 'settings__location__configurations',
        label: t('core:locationPermissions.access.configurations', 'Configurations'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__operations',
        label: t('core:locationPermissions.access.operations', 'Operations'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__checkin',
        label: t('core:locationPermissions.access.checkin', 'Check-in'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__communication',
        label: t('core:locationPermissions.access.communication', 'Communication'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__social_media',
        label: t('core:locationPermissions.access.socialMedia', 'Social media'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__member_alerts',
        label: t('core:locationPermissions.access.memberAlerts'),
        info: t('core:locationPermissions.access.memberAlertsInfo'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__policy_gating',
        label: t('core:locationPermissions.access.policyGating'),
        info: t('core:locationPermissions.access.policyGatingInfo'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__policy_import',
        label: t('core:locationPermissions.access.policyImport'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__policy_import_trusted',
        label: t('core:locationPermissions.access.policyImportTrusted'),
        info: t('core:locationPermissions.access.policyImportTrustedInfo'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__website_widgets',
        label: t('core:locationPermissions.access.websiteWidgets'),
        parent: 'settings__location',
      },
      {
        key: 'settings__location__integrations',
        label: t('core:locationPermissions.access.integrationsAndTools', 'Integrations and Tools'),
        parent: 'settings__location',
      },
      {
        key: 'audit__read',
        label: t('core:locationPermissions.access.auditHistory'),
        parent: 'settings__location',
      },
    ],
  },
  {
    key: 'settings__scheduler',
    label: t('core:locationPermissions.access.scheduler', 'Scheduler'),
    children: [
      {
        key: 'settings__scheduler__rules',
        label: t('core:locationPermissions.access.rules'),
        parent: 'settings__scheduler',
      },
      {
        key: 'settings__scheduler__session_types',
        label: t('core:locationPermissions.access.sessionTypes', 'Session types'),
        parent: 'settings__scheduler',
      },
      {
        key: 'settings__scheduler__session_templates',
        label: t('core:locationPermissions.access.sessionTemplates'),
        parent: 'settings__scheduler',
      },
      {
        key: 'settings__scheduler__calendar_configurations',
        label: t(
          'core:locationPermissions.access.calendarConfigurations',
          'Calendar configurations'
        ),
        parent: 'settings__scheduler',
      },
    ],
  },
  {
    key: 'access__clients',
    label: t('core:locationPermissions.access.clients', 'Clients'),
    children: [
      {
        key: 'clients__client_merge',
        label: t('core:locationPermissions.access.clientMerge', 'Client merge'),
        parent: 'access__clients',
      },
      {
        key: 'clients__client_password_reset',
        label: t(
          'core:locationPermissions.retailPayments.clientPasswordReset',
          'Client password reset'
        ),
        parent: 'access__clients',
      },
      {
        key: 'clients__delete',
        label: t('core:locationPermissions.access.deleteClients', 'Delete clients'),
        parent: 'access__clients',
      },
      {
        key: 'clients__owed_balance_block__override',
        label: t('core:locationPermissions.access.owedBalanceBlockOverride'),
        parent: 'access__clients',
      },
      {
        key: 'clients__export_history',
        label: t('core:locationPermissions.access.exportHistory'),
        info: t('core:locationPermissions.access.exportHistoryInfo'),
        parent: 'access__clients',
      },
      {
        key: 'clients__policy_forms__record_paper',
        label: t('core:locationPermissions.access.policyFormsRecordPaper'),
        parent: 'access__clients',
      },
      {
        key: 'clients__policy_forms__approve_paper',
        label: t('core:locationPermissions.access.policyFormsApprovePaper'),
        info: t('core:locationPermissions.access.policyFormsApprovePaperInfo'),
        parent: 'access__clients',
      },
      {
        key: 'clients__policy_forms__view_evidence',
        label: t('core:locationPermissions.access.policyFormsViewEvidence'),
        parent: 'access__clients',
      },
      {
        key: 'clients__policy_forms__void',
        label: t('core:locationPermissions.access.policyFormsVoid'),
        parent: 'access__clients',
      },
      {
        key: 'clients__view_member_as_admin',
        label: t(
          'core:locationPermissions.access.viewMemberAsAdmin',
          "View members' app as the member"
        ),
        info: t(
          'core:locationPermissions.access.viewMemberAsAdminInfo',
          'Lets the user sign in as members connected to this location. Viewing is recorded. Has no effect until the brand turns on "Allow location staff to view member apps".'
        ),
        parent: 'access__clients',
      },
    ],
  },
  {
    key: 'wellness__equipments__manage',
    label: t(
      'core:locationPermissions.wellness.equipmentsManage',
      'Allow users to edit wellness settings'
    ),
  },
  {
    key: 'wellness__equipment_lamps__manage',
    label: t(
      'core:locationPermissions.wellness.equipmentLampsManage',
      'Allow users to track lamp hours and log lamp changes'
    ),
  },
  {
    key: 'wellness__equipment_test_time__override',
    label: t(
      'core:locationPermissions.wellness.equipmentTestTimeOverride',
      'Allow users to change the Test Time on equipment'
    ),
  },
  {
    key: 'wellness__equipment_skin_type__override',
    label: t('core:locationPermissions.wellness.equipmentSkinTypeOverride'),
  },
  {
    key: 'wellness__equipment_skin_type__assign',
    label: t('core:locationPermissions.wellness.equipmentSkinTypeAssign'),
  },
  {
    key: 'wellness__equipment_eligibility__view',
    label: t('core:locationPermissions.wellness.equipmentEligibilityView'),
  },
]

// Reporting Permissions (5 total)
export const REPORTING_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'reports__client_and_attendance',
    label: t('core:locationPermissions.reporting.clientsAndAttendance', 'Clients & attendance'),
  },
  {
    key: 'reports__staff',
    label: t('core:locationPermissions.reporting.staff', 'Staff'),
  },
  {
    key: 'reports__financial',
    label: t('core:locationPermissions.reporting.financial', 'Financial'),
  },
  {
    key: 'reports__commissions',
    label: t('core:locationPermissions.reporting.commissions', 'Commissions'),
  },
  {
    key: 'commissions__adjust',
    label: t('core:locationPermissions.reporting.commissionsAdjust', 'Adjust commission'),
  },
  {
    key: 'commissions__attribution__edit',
    label: t(
      'core:locationPermissions.reporting.commissionsAttributionEdit',
      'Change who earns commission on a sale'
    ),
  },
  {
    key: 'reports__inventory',
    label: t('core:locationPermissions.reporting.inventory'),
  },
  {
    key: 'reports__policy_evidence_export',
    label: t('core:locationPermissions.reporting.policyEvidenceExport'),
    info: t('core:locationPermissions.reporting.policyEvidenceExportInfo'),
  },
  {
    key: 'reports__download',
    label: t('core:locationPermissions.reporting.downloadReports', 'Download Reports'),
  },
  {
    key: 'payments__payroll_report_access',
    label: t(
      'core:locationPermissions.retailPayments.payrollReportAccess',
      'Payroll report access'
    ),
  },
  {
    key: 'reports__edit_restricted_reports',
    label: t('core:locationPermissions.access.editOfRestrictedReport', 'Edit of restricted report'),
  },
]

export const REPORT_DATA_PRIVACY_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'site_pii__view_full_identity',
    label: t('core:locationPermissions.reportDataPrivacy.viewFullIdentity', 'View full identity'),
    info: t(
      'core:locationPermissions.reportDataPrivacy.viewFullIdentityInfo',
      'Allows viewing full names and exact dates of birth in reports for this location.'
    ),
  },
  {
    key: 'site_pii__view_contact_details',
    label: t(
      'core:locationPermissions.reportDataPrivacy.viewContactDetails',
      'View contact details'
    ),
    info: t(
      'core:locationPermissions.reportDataPrivacy.viewContactDetailsInfo',
      'Allows viewing email addresses and phone numbers in reports for this location.'
    ),
  },
  {
    key: 'site_pii__export_pii',
    label: t('core:locationPermissions.reportDataPrivacy.exportPii', 'Export PII'),
    info: t(
      'core:locationPermissions.reportDataPrivacy.exportPiiInfo',
      'Allows including personal data in CSV/Excel exports for this location.'
    ),
  },
]

// Import Permissions (6 total)
export const IMPORT_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'import__initiate',
    label: t('core:locationPermissions.import.initiate', 'Initiate imports'),
    info: t(
      'core:locationPermissions.import.initiateInfo',
      'Upload CSV files, edit preview rows, reupload files, and update location mapping'
    ),
  },
  {
    key: 'import__preview',
    label: t('core:locationPermissions.import.preview', 'View imports'),
    info: t(
      'core:locationPermissions.import.previewInfo',
      'View migration overview, import sessions, and check prerequisites'
    ),
  },
  {
    key: 'import__approve_and_execute',
    label: t('core:locationPermissions.import.approveAndExecute', 'Approve and execute'),
    info: t(
      'core:locationPermissions.import.approveAndExecuteInfo',
      'Approve validated data and trigger import execution'
    ),
  },
  {
    key: 'import__delete',
    label: t('core:locationPermissions.import.delete', 'Delete imports'),
    info: t(
      'core:locationPermissions.import.deleteInfo',
      'Delete migrations and remove uploaded files'
    ),
  },
  {
    key: 'import__download_source',
    label: t('core:locationPermissions.import.downloadSource', 'Download source files'),
    info: t(
      'core:locationPermissions.import.downloadSourceInfo',
      'Download the original uploaded CSV files'
    ),
  },
  {
    key: 'import__download_audit',
    label: t('core:locationPermissions.import.downloadAudit', 'Download audit reports'),
    info: t(
      'core:locationPermissions.import.downloadAuditInfo',
      'Download audit CSV reports after import execution'
    ),
  },
]

export const CONTENT_SUITE_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'content__create',
    label: t('core:locationPermissions.contentSuite.create'),
  },
  {
    key: 'content__edit_own',
    label: t('core:locationPermissions.contentSuite.editOwn'),
  },
  {
    key: 'content__publish_own',
    label: t('core:locationPermissions.contentSuite.publishOwn'),
  },
  {
    key: 'content__delete_own',
    label: t('core:locationPermissions.contentSuite.deleteOwn'),
  },
  {
    key: 'content__view_corporate',
    label: t('core:locationPermissions.contentSuite.viewCorporate'),
  },
]

/**
 * OC-1461 — rolling APPROVAL mode. Site-scoped on purpose: the posture exists so a location
 * controls its own intake without seeing the rest of the brand, which is why it is not folded into
 * the corporate `member_movement__approve`. Server-enforced by
 * `IncomingApprovalService.permittedDestinationSiteIds`, so it belongs in the visible catalogue —
 * without an entry here the bit exists and is backfilled but cannot be granted to the
 * location-manager policy the whole posture is for.
 */
export const MEMBER_MOVEMENT_PERMISSIONS_LOCATION: PermissionDefinition[] = [
  {
    key: 'member_movement__approve_incoming',
    label: t(
      'core:locationPermissions.memberMovement.approveIncoming',
      'Approve incoming transfers'
    ),
    info: t(
      'core:locationPermissions.memberMovement.approveIncomingInfo',
      'Accept or decline members qualified to transfer into this location under rolling approval mode'
    ),
  },
  {
    key: 'member_movement__manage_incoming',
    label: t('core:locationPermissions.memberMovement.manageIncoming'),
    info: t('core:locationPermissions.memberMovement.manageIncomingInfo'),
  },
]

export const FLAG_GATED_LOCATION_PERMISSIONS: Record<string, FeatureFlagKeys[]> = {
  clients__view_member_as_admin: [
    FeatureFlagKeys.MemberViewAsAdmin,
    FeatureFlagKeys.MemberViewLocationAccess,
  ],
}

export const LOCATION_PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'schedule',
    title: t('core:locationPermissions.categories.schedule', 'Schedule'),
    icon: 'calendar',
    saveButtonLabel: t(
      'core:locationPermissions.saveButtons.schedule',
      'Save schedule permissions'
    ),
    permissions: SCHEDULE_PERMISSIONS,
  },
  {
    id: 'retailPayments',
    title: t('core:locationPermissions.categories.retailPayments', 'Retail & Payments'),
    icon: 'retail',
    saveButtonLabel: t(
      'core:locationPermissions.saveButtons.retailPayments',
      'Save retail & payments permissions'
    ),
    permissions: RETAIL_PAYMENTS_PERMISSIONS,
  },
  {
    id: 'access',
    title: t('core:locationPermissions.categories.access', 'Access'),
    icon: 'key',
    saveButtonLabel: t('core:locationPermissions.saveButtons.access', 'Save access permissions'),
    permissions: ACCESS_PERMISSIONS_LOCATION,
  },
  {
    id: 'reporting',
    title: t('core:locationPermissions.categories.reporting', 'Reporting'),
    icon: 'chart',
    saveButtonLabel: t(
      'core:locationPermissions.saveButtons.reporting',
      'Save reporting permissions'
    ),
    permissions: REPORTING_PERMISSIONS,
  },
  {
    id: 'reportDataPrivacy',
    title: t('core:locationPermissions.categories.reportDataPrivacy', 'Report Data Privacy'),
    icon: 'key',
    saveButtonLabel: t(
      'core:locationPermissions.saveButtons.reportDataPrivacy',
      'Save report data privacy permissions'
    ),
    permissions: REPORT_DATA_PRIVACY_PERMISSIONS,
  },
  {
    id: 'import',
    title: t('core:locationPermissions.categories.import', 'Import'),
    icon: 'database',
    saveButtonLabel: t('core:locationPermissions.saveButtons.import', 'Save import permissions'),
    permissions: IMPORT_PERMISSIONS,
  },
  {
    id: 'memberMovement',
    title: t('core:locationPermissions.categories.memberMovement', 'Member Movement'),
    icon: 'key',
    saveButtonLabel: t(
      'core:locationPermissions.saveButtons.memberMovement',
      'Save member movement permissions'
    ),
    permissions: MEMBER_MOVEMENT_PERMISSIONS_LOCATION,
  },
]

// Content Suite at location level is intentionally not exposed yet: content enforcement is
// brand-tier only, so a location toggle would offer actions the server rejects. Re-add this
// category to LOCATION_PERMISSION_CATEGORIES when the server enforces these bits. The
// definitions are kept so a policy that already holds one still gets a label in read-only views
// and in the assignability panel (OC-1222).
export const HIDDEN_LOCATION_PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'contentSuite',
    title: t('core:locationPermissions.categories.contentSuite'),
    icon: 'key',
    saveButtonLabel: t('core:locationPermissions.saveButtons.contentSuite'),
    permissions: CONTENT_SUITE_PERMISSIONS,
  },
]

export const LABELLED_LOCATION_PERMISSION_CATEGORIES: PermissionCategory[] = [
  ...LOCATION_PERMISSION_CATEGORIES,
  ...HIDDEN_LOCATION_PERMISSION_CATEGORIES,
]

export const LOCATION_PERMISSION_FIELD_NAMES = {
  SITE_PERMISSIONS: 'sitePermissions',
} as const
